export type AgentPlatformShellVariant =
  | 'default'
  | 'hub'
  | 'composer'
  | 'studio'
  | 'governance';

/** Área funcional exibida no badge do cabeçalho (sem referência a produtos de terceiros). */
export type AgentPlatformArea =
  | 'assistants'
  | 'tools'
  | 'knowledge'
  | 'workflow'
  | 'graph'
  | 'governance'
  | 'operations';

export const agentPlatformAreaLabels: Record<AgentPlatformArea, string> = {
  assistants: 'Assistentes + Agentes',
  tools: 'Tools',
  knowledge: 'Conhecimento',
  workflow: 'Workflow',
  graph: 'Orquestração visual',
  governance: 'Compliance',
  operations: 'Operações',
};

export const apTheme = {
  brand: {
    cement: '#363130',
    cementDark: '#242120',
    cementLight: '#4a4543',
    red: '#c0392b',
    redDark: '#96281b',
  },
  shell: {
    default:
      'min-h-full bg-gradient-to-br from-stone-100 via-zinc-50 to-stone-100',
    hub: 'min-h-full bg-stone-100',
    composer: 'min-h-[calc(100vh-4rem)] flex flex-col bg-[#2c2826] text-slate-100',
    studio: 'min-h-full bg-gradient-to-br from-stone-50 via-white to-red-50/30',
    governance: 'min-h-full bg-gradient-to-br from-stone-50 via-white to-zinc-100',
  },
  hero: {
    hub: 'relative overflow-hidden bg-gradient-to-r from-[#121110] via-[#242120] to-[#363433] text-white px-6 py-10 sm:py-14',
    subtitle: 'text-stone-300/90',
  },
  panel:
    'rounded-2xl border border-stone-200/80 bg-white/95 backdrop-blur-sm shadow-lg shadow-stone-300/40',
  panelInset: 'rounded-xl border border-stone-100 bg-stone-50/80',
  input:
    'w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 shadow-sm transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-500/20',
  tableWrap:
    'overflow-hidden rounded-2xl border border-stone-200/80 bg-white shadow-lg shadow-stone-200/40',
  tableHead:
    'bg-gradient-to-r from-stone-50 to-red-50/40 text-xs font-semibold uppercase tracking-wide text-stone-500',
  tableActionsHead: 'px-4 py-3 text-center w-36 whitespace-nowrap',
  tableActionsCell: 'px-4 py-3 align-middle',
  tableActionsGroup: 'inline-flex flex-nowrap items-center justify-center gap-2',
  tableRowHover: 'hover:bg-red-50/40 transition-colors',
  tableRowSelected: 'bg-red-50/60',
  link: 'text-red-700 hover:text-red-900',
  tabActive: 'bg-white text-red-800 shadow-sm ring-1 ring-red-100',
  tabIdle: 'text-stone-600 hover:bg-white/60 hover:text-stone-900',
  composerSidebar: 'w-56 shrink-0 border-r border-white/10 bg-[#242120] p-3',
  composerCanvas:
    'flex-1 bg-[#363130] bg-[radial-gradient(circle_at_1px_1px,_rgba(255,255,255,0.06)_1px,_transparent_0)] bg-[length:24px_24px]',
  composerToolbar: 'flex items-center gap-3 border-b border-white/10 bg-[#242120] px-4 py-3',
  composerSurface: 'rounded-xl border border-white/10 bg-[#242120]',
  composerInput:
    'w-full border border-white/10 rounded-lg px-3 py-2 bg-[#363130] text-slate-200 focus:border-red-500/50 focus:ring-1 focus:ring-red-500/30',
  studioCanvas:
    'rounded-2xl border border-stone-200/60 bg-gradient-to-br from-stone-50/80 to-white p-6 shadow-inner shadow-stone-200/50',
  governancePolicy:
    'rounded-2xl border border-stone-200/60 bg-gradient-to-br from-white to-stone-50/80 p-6 shadow-md shadow-stone-200/30',
  statCard:
    'rounded-2xl border border-stone-200/80 bg-white/95 backdrop-blur-sm p-5 shadow-md shadow-stone-200/40',
  formPanel:
    'rounded-2xl border border-stone-200/80 bg-white/95 backdrop-blur-sm p-5 shadow-lg shadow-stone-200/40 grid gap-3',
  chat: {
    user: 'ml-auto bg-gradient-to-br from-[#4a4543] to-[#c0392b] text-white shadow-md shadow-red-900/20',
    assistant: 'bg-white border border-stone-200/80 text-stone-800 shadow-sm',
    error: 'bg-red-50 text-red-800 border border-red-200',
    container:
      'rounded-2xl border border-stone-200/80 bg-gradient-to-b from-white to-stone-50/80 shadow-xl shadow-stone-200/50 flex flex-col min-h-[480px]',
  },
  badge: {
    assistants: 'bg-red-50 text-red-900 ring-1 ring-red-100',
    tools: 'bg-slate-100 text-slate-800 ring-1 ring-slate-200',
    knowledge: 'bg-violet-50 text-violet-900 ring-1 ring-violet-100',
    workflow: 'bg-stone-100 text-red-800 ring-1 ring-stone-200',
    graph: 'bg-stone-100 text-stone-800 ring-1 ring-stone-200',
    governance: 'bg-stone-100 text-stone-700 ring-1 ring-stone-200',
    operations: 'bg-sky-50 text-sky-900 ring-1 ring-sky-100',
  },
} as const;

export type HubCardIconTone =
  | 'red'
  | 'stone'
  | 'emerald'
  | 'slate'
  | 'amber'
  | 'teal'
  | 'sky'
  | 'zinc'
  | 'green'
  | 'purple';

export const hubCardIconStyles: Record<HubCardIconTone, string> = {
  red: 'bg-[#c0392b] text-white shadow-md shadow-red-900/15',
  stone: 'bg-stone-600 text-white shadow-md shadow-stone-900/10',
  emerald: 'bg-emerald-600 text-white shadow-md shadow-emerald-900/15',
  slate: 'bg-slate-600 text-white shadow-md shadow-slate-900/10',
  amber: 'bg-amber-600 text-white shadow-md shadow-amber-900/15',
  teal: 'bg-teal-600 text-white shadow-md shadow-teal-900/15',
  sky: 'bg-sky-600 text-white shadow-md shadow-sky-900/15',
  zinc: 'bg-zinc-700 text-white shadow-md shadow-zinc-900/10',
  green: 'bg-green-600 text-white shadow-md shadow-green-900/15',
  purple: 'bg-violet-600 text-white shadow-md shadow-violet-900/15',
};

export const hubCardHoverShadow: Record<HubCardIconTone, string> = {
  red: 'group-hover:shadow-red-200/60',
  stone: 'group-hover:shadow-stone-300/60',
  emerald: 'group-hover:shadow-emerald-200/60',
  slate: 'group-hover:shadow-slate-300/60',
  amber: 'group-hover:shadow-amber-200/60',
  teal: 'group-hover:shadow-teal-200/60',
  sky: 'group-hover:shadow-sky-200/60',
  zinc: 'group-hover:shadow-zinc-300/60',
  green: 'group-hover:shadow-green-200/60',
  purple: 'group-hover:shadow-violet-200/60',
};

export function shellClass(variant: AgentPlatformShellVariant): string {
  return apTheme.shell[variant];
}
